import { useMsal } from "@azure/msal-react";
import {
  getSignInDomain,
  isSignInDomainSelectable,
  setSignInDomain,
  signInDomainOptions,
  signInExtraQueryParameters,
  type SignInDomainMode,
} from "@eaigle/api/signInDomain";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../Select";

interface LoginButtonProps {
  className?: string;
}

const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";

export const LoginButton = ({ className }: LoginButtonProps) => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [domainMode, setDomainMode] = useState<SignInDomainMode>(getSignInDomain);

  const handleLogin = () => {
    // Save current URL for redirect after login (if not already on root)
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== "/") {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, currentPath);
    }
    // Resolved here rather than at module scope so the picked domain is applied.
    // TODO: Implement login error handling
    void instance.loginRedirect({
      scopes: [],
      extraQueryParameters: signInExtraQueryParameters(),
    });
  };

  const loginButton = (
    <button
      id="LoginButton"
      onClick={handleLogin}
      className={`px-6 py-3 bg-primary text-white text-lg font-semibold rounded-lg 
                 shadow-lg hover:bg-opacity-90 focus:outline-none focus:ring-2 
                 focus:ring-secondary focus:ring-opacity-50 transition-all duration-200 ${className || ""}`}
    >
      {t("COMMAND.SSO_LOGIN")}
    </button>
  );

  if (!isSignInDomainSelectable()) return loginButton;

  const handleDomainChange = (value: string) => {
    const mode = value as SignInDomainMode;
    setDomainMode(mode);
    setSignInDomain(mode);
  };

  return (
    <div className="inline-flex flex-col items-center gap-4">
      <div className="flex w-full flex-col items-start gap-1 text-left">
        <label htmlFor="LoginDomainSelect" className="text-sm text-white">
          {t("LABEL.SIGN_IN_DOMAIN")}
        </label>
        <Select value={domainMode} onValueChange={handleDomainChange}>
          <SelectTrigger
            id="LoginDomainSelect"
            className="w-full max-w-none items-center rounded-lg"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="w-[var(--radix-select-trigger-width)] rounded-lg shadow-lg">
            <SelectGroup>
              {signInDomainOptions().map((option) => (
                <SelectItem key={option.mode} value={option.mode} className="rounded-md">
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      {loginButton}
    </div>
  );
};
